// Load polyfills first, before any other code runs
import '$lib/utils/polyfills';

import * as Sentry from '@sentry/sveltekit';
import type { HandleClientError } from '@sveltejs/kit';
import { dev } from '$app/environment';
import { getSentryConfig, beforeSendFilter } from '$lib/monitoring/sentry';

const environment = dev ? 'development' : 'production';

// Use the enhanced getSentryConfig which now handles PUBLIC_ variables
const clientSentryConfig = getSentryConfig(environment);

// Auto-detect release from DigitalOcean or fallback options
const release =
	process.env.SENTRY_RELEASE ||
	process.env.COMMIT_HASH || // DigitalOcean provides this
	process.env.DO_APP_COMMIT_SHA || // Alternative DO variable
	`${process.env.npm_package_name}@${process.env.npm_package_version}` ||
	'unknown';

Sentry.init({
	environment,
	dsn:
		process.env.SENTRY_DSN ??
		'https://ee583c4d608296a71271330b6dfecb4b@o4509525984149504.ingest.us.sentry.io/4509525985656832',
	release,

	// Performance Monitoring
	tracesSampleRate: clientSentryConfig.tracesSampleRate,
	tracePropagationTargets: clientSentryConfig.tracePropagationTargets,

	// Session Replay
	replaysSessionSampleRate: clientSentryConfig.replaysSessionSampleRate,
	replaysOnErrorSampleRate: clientSentryConfig.replaysOnErrorSampleRate,

	// Integrations
	integrations: [
		// User feedback widget - manual trigger only
		Sentry.feedbackIntegration({
			colorScheme: 'system',
			showBranding: true,
			showName: true,
			showEmail: true,
			isNameRequired: false,
			isEmailRequired: true,
			// Remove redundant useSentryUser mapping since we manually set user context
			buttonLabel: 'Feedback',
			submitButtonLabel: 'Send Feedback',
			formTitle: 'Send Feedback to LocallyGrown.net',
			autoInject: false, // Disable auto-injection for manual trigger
		}),
		// Browser tracing for performance monitoring
		Sentry.browserTracingIntegration({
			enableInp: true, // Enable Interaction to Next Paint
			enableLongTask: true, // Track long tasks
		}),
		// Session replay with privacy settings
		Sentry.replayIntegration({
			maskAllText: false, // Don't mask all text by default
			maskAllInputs: true, // Mask input fields for privacy
			blockAllMedia: false, // Allow media replay
			// Mask sensitive selectors
			mask: [
				'[data-sentry-mask]',
				'input[type="password"]',
				'input[type="email"]',
				'input[name*="card"]',
				'input[name*="credit"]',
				'[data-sensitive]',
			],
			// Block specific selectors from replay
			block: ['.stripe-input', '[data-sentry-block]'],
		}),
	],

	// Error filtering
	beforeSend: beforeSendFilter,
	ignoreErrors: clientSentryConfig.ignoreErrors,
	denyUrls: clientSentryConfig.denyUrls,

	// Development settings
	debug: clientSentryConfig.debug,
	enabled: clientSentryConfig.enabled,

	// Attachments
	maxValueLength: clientSentryConfig.maxValueLength,
	normalizeDepth: clientSentryConfig.normalizeDepth,

	// Initial scope configuration
	initialScope: {
		tags: {
			component: 'client',
			runtime: 'browser',
			deploy_platform: 'digitalocean',
		},
		contexts: {
			browser: {
				viewport: {
					width: window.innerWidth,
					height: window.innerHeight,
				},
			},
		},
	},
});

export const handleError: HandleClientError = Sentry.handleErrorWithSentry(
	async ({ error, event }) => {
		console.error('Client-side error:', error);

		// Log the error details for debugging
		const errorDetails = {
			name: error instanceof Error ? error.name : 'Unknown',
			message: error instanceof Error ? error.message : String(error),
			stack: error instanceof Error ? error.stack : undefined,
			path: event.url.pathname,
			url: event.url.toString(),
			userAgent: navigator.userAgent,
			referer: document.referrer,
		};

		// In development, log to console
		if (process.env.NODE_ENV === 'development') {
			console.error('Client error details:', errorDetails);
		}

		// In production, send to server for OpenSearch logging
		if (process.env.NODE_ENV === 'production') {
			try {
				await fetch('/api/log-error', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(errorDetails),
				});
			} catch (err) {
				console.error('Failed to send error to server:', err);
			}
		}

		return {
			message: 'An error occurred',
			code: 'CLIENT_ERROR',
		};
	}
);
